import { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useLocation, useNavigate } from "react-router-dom";
import "./checkuserinactivity.css";
import { useGetOrgIdentifier } from "../generic/hooks/useGetOrgIdentifier";
import { performPostRequest } from "../../service/network/NetworkService";
import IntentType from "../../intentexecutor/utils/IntentType";
import { getApiUrl } from "../../service/network/UrlMapper";
import ErrorNotificationComponent from "../../components/generic/error/ErrorNotificationComponent";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { currentUserTimeout } from "../../components/recoil/userTimeoutRecoil";
import MemoryType from "../../service/memory/enum/MemoryType";
import { userDetails } from "../../components/recoil/userDataRecoil";
import { clearLocalStorage } from "../auth/logout/utils";
import { publishEvent } from "../../components/generic/EventPublisher";
import { useReadMemory } from "../generic/hooks/useReadMemory";
import apiService from "../../service/apiservice/apiService";
import { activateIdleTimer } from "../../components/recoil/activateIdleTimerRecoil";

const CheckUserInactivity = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [lockTimeout, setLockTimeout] = useState(600000);
  const [timeout, setTimeout] = useState(1800000);
  const navigate = useNavigate();
  const userTimeout = useRecoilValue(currentUserTimeout);
  const setUserDetails = useSetRecoilState(userDetails);
  const userData = useReadMemory(MemoryType.USER);
  const activateTimer = useRecoilValue(activateIdleTimer);
  const location = useLocation();

  const onPrompt = () => {
    localStorage.removeItem(MemoryType.AUTH);
    const currentPath = location.pathname + location.search;
    navigate("/authenticate?inactivity=true", {
      replace: true,
      state: { from: currentPath },
    });
  };

  const onIdle = () => {
    const logout = async () => {
      const deleteEndpoint = "event/device/v1/" + userData?.email || "";
      await apiService.delete(deleteEndpoint);
      publishEvent("niramaya.user.logout", userData);
      clearLocalStorage();
      setUserDetails({});
      navigate("/login");
    };
    logout();
  };

  const { activate, reset } = useIdleTimer({
    onIdle,
    onPrompt,
    timeout: timeout,
    promptBeforeIdle: timeout - lockTimeout,
    throttle: 500,
  });

  useEffect(() => {
    activate();
    reset();
  }, [activate, reset, activateTimer]);

  useEffect(() => {
    const getConfigData = async () => {
      const configPayload = {
        intent: "get_configuration",
        payload: {
          configurationKey: ["niramaya.dashboarddata.keys"],
        },
        context: {
          organizationIdentifier,
        },
      };

      try {
        const response = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(configPayload)
        );
        const result = await response.json();

        const dashboardKeyData = JSON.parse(
          result[0]?.configurationValue || "[]"
        );

        const handlers = {
          TIMEOUT_IN_MINUTE: setTimeout,
          LOCK_TIMEOUT_IN_MINUTE: setLockTimeout,
        };

        for (const { key, value } of dashboardKeyData || []) {
          const handler = handlers[key];
          if (handler) {
            handler(value * 60 * 1000);
          }
        }
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };
    getConfigData();
  }, [organizationIdentifier, userTimeout]);

  return null;
};

export default CheckUserInactivity;
